/**
 * Deal Dossier — Property dashboard.
 *
 * One responsive workspace, with no nested Property navigation. Every value
 * is sourced from the current deal, its uploaded documents/activity, the map
 * configuration, or the live comparables service. Missing records remain
 * explicit instead of being filled with a sample property.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Map as MapIcon,
  Table2,
  ArrowRight,
  Building2,
  CalendarClock,
  DollarSign,
  Gauge,
  Home,
  Loader2,
  GraduationCap,
  HeartPulse,
  LandPlot,
  ParkingSquare,
  Pill,
  School,
  ShoppingBag,
  ShoppingCart,
  TrainFront,
  Trees,
  TrendingUp,
  Users,
  Utensils,
  Waves,
  type LucideIcon,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import type { PropertyProfile, PropertyPhoto, UnitMix } from '@/types/propertyProfile'
import type { DealDocument } from '@/services/documents'
import { receivedByEmail } from './documentsModel'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import type { DealDossierTab } from '@/store/dealDossierStore'
import { compsService, type CompRecord } from '@/services/comps'
import {
  NEARBY_CATEGORY_LABELS,
  type DossierAddress,
  type DossierComparable,
  type DossierLinkBasis,
  type DossierPropertyMaster,
  type IntelSource,
  type PropertySiteIntel,
} from '@/services/dossierContext'
import { AddressLink } from '@/components/address/AddressLink'
import { mapsUrl } from '@/components/address/mapsUrl'
import { PropertyMap } from './PropertyMap'
import { ADDRESS_PENDING_TEXT, STATE_ONLY_NOTE, type AssetPointStatus } from './assetCoordinate'
import { CompsMap, type MapPoint } from './CompsMap'
import { AERIAL_IMAGERY, aerialSnapshotUrl } from './aerialImagery'
import { CONDITION_ICON, ENVIRONMENTAL_ICON, FloodZoneTile, ReportStatusTile } from './RiskQualityTiles'
import { LocationIntelligenceDetail } from './LocationIntelligenceDetail'
import { CrimeSafetyPanel } from './CrimeSafetyPanel'
import { MediaGallery } from './MediaGallery'
import { DossierPanel, EditableSlot, EmptyLine, StackedField } from './ui'
import { ADDRESS_CONFIRM_REASON, ADDRESS_EDIT_TARGETS, type SlotKind } from './editableFields'
import { EditableValue } from './EditableValue'
import { useDossierEdit } from './editContext'
import { ValueStateChip } from './valueStateChip'
import type { FieldSourceRef } from './workspace/types'
import { isLodging, keysLabel } from './lodging'
import { isOccupiedRow, plausibleCapRate } from '../dossierProfiles'
import { formatAmount, formatDate, formatMoney, formatMultiple, formatPct, textOrDash } from './format'
import { parseDateValue } from '@/utils/dates'
import { plainRefusal } from '@/utils/plainRefusal'

type Props = {
  /** The property's own website — one of the three photo sources, and the
   *  only one a person can supply from this page. */
  website?: string | null
  /** Point the photo lookup at a website. */
  onLinkPhotoSource?: (url: string) => void
  loanId: string
  defs: DealFieldDef[]
  raw: RawDealFile
  profile: PropertyProfile
  address: string
  photos: PropertyPhoto[]
  documents: DealDocument[]
  activity: LoanActivityEvent[]
  mapsKey: string | null
  mapsConfigured: boolean
  mapsError: string | null
  mapsLoading?: boolean
  /** The server's say-so on aerial imagery; anything but `true` keeps Aerial off. */
  mapsAerialEnabled?: boolean
  mediaNotes?: string[]
  mediaLoading?: boolean
  formattedAddress?: string | null
  /** The building's coordinate. Set only for a street a person or document stands behind. */
  mapLat?: number | null
  mapLng?: number | null
  /**
   * What the coordinate above is a point on (see `assetCoordinate.ts`). Absent
   * reads as `asset`, which is how a caller that never learned the distinction
   * behaved before it existed.
   */
  mapBasis?: AssetPointStatus
  /** A city-level point, for centring the map when no building is placed. */
  cityLat?: number | null
  cityLng?: number | null
  /** The property master this deal resolves to, when one exists. */
  propertyMaster?: DossierPropertyMaster | null
  /** How the master was linked: by the deal's `property_id`, or by name and
   *  market. A name link is a guess a reader should know about (UI-09). */
  propertyMatchedBy?: DossierLinkBasis
  /** The address as the server settled it, and where each part came from. */
  addressBlock?: DossierAddress | null
  /**
   * The city and state the dossier settled on, with name-lookup values
   * withheld. The comps market and the crime panel read these instead of the
   * deal file's raw city, state and location, which on a legacy deal are the
   * building's name resolved by Google Places.
   */
  addressParts?: { city: string | null; state: string | null } | null
  /** The address could not be checked against the package (its provenance did not load). */
  addressUnchecked?: boolean
  /** The dossier context that settles the address has not answered yet: say so, not "Not on record". */
  addressPending?: boolean
  /** The settled address is a state and nothing else (a lookup's street and city were set aside). */
  addressStateOnly?: boolean
  /**
   * Unlocked, the address may be opened as its street, city, state and postal
   * code: a street a document or a person stands behind is on screen (the
   * dossier's one rule, shared with the header and the Overview). Absent: not
   * editable — the deal file's street may be a name lookup's guess.
   */
  addressEditable?: boolean
  /** County, flood zone, demographics, transit, schools, the site's website. */
  siteIntel?: PropertySiteIntel | null
  siteIntelLoading?: boolean
  /** Comps for this asset type in this deal's market, resolved server-side. */
  comparables?: DossierComparable[] | null
  /** Comp tables the package states that the server could not read. */
  packageCompTablesUnread?: { document: string; heading: string }[]
  /**
   * The market prose the intake engine parsed from the package, read off the
   * linked record while nothing has promoted it onto the deal. The deal's own
   * `market_narrative` (on the profile) wins when both exist (UI-01).
   */
  marketNarrative?: string | null
  onGoto: (tab: DealDossierTab) => void
}

type OccupancyPoint = { label: string; occupancy: number }
type UnitMixRow = Pick<UnitMix, 'type' | 'count'> & {
  avgRent: number | null
  occupancy: number | null
}
/** Occupancy as the rent roll itself states it, on the one date it covers. */
export type RentRollOccupancy = {
  /** What each bar is: a floor plan's let share, or a status's share of the roll. */
  basis: 'floor plan' | 'status'
  rows: { label: string; pct: number; count: number; of: number }[]
  /** Let units over all units on the roll. */
  overallPct: number
}
export type LeaseExpirationRow = { year: string; pct: number; basis: 'gross rent' | 'area' }
export type RecentPropertyActivity = {
  id: string
  at: string | null
  title: string
  actor: string | null
  detail: string | null
  kind: 'activity' | 'document'
}

const NOT_ON_RECORD = 'Not on record'
const GOLD = '#d9b768'
const STONE = '#78716c'
const UNIT_COLORS = ['#d9b768', '#8b6427', '#78716c', '#57534e', '#a8a29e']

function rawValue(raw: RawDealFile, key: string): unknown {
  const direct = raw[key]
  if (direct !== undefined && direct !== null && direct !== '') return direct
  const custom = raw.custom_fields
  if (custom && typeof custom === 'object') {
    const value = (custom as Record<string, unknown>)[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return undefined
}

function numeric(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function percentNumber(value: unknown): number | null {
  if (typeof value === 'string') {
    const parsed = Number(value.replace('%', '').trim())
    return Number.isFinite(parsed) ? parsed : null
  }
  const parsed = numeric(value)
  if (parsed == null) return null
  return Math.abs(parsed) <= 1.5 ? parsed * 100 : parsed
}

function recordedText(value: unknown): string | null {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  if (!text || text === '—' || text.toLowerCase() === NOT_ON_RECORD.toLowerCase()) return null
  return text
}

function humanize(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function detailText(details: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = recordedText(details[key])
    if (value) return value
  }
  return null
}

const LEADS_WITH_NUMBER_RE = /^\s*\d/

/**
 * The city and state a location names — never its street.
 *
 * "Austin, TX" → ["Austin", "TX"]; "1200 Fifth Avenue North, Nashville, TN
 * 37208" → ["Nashville", "TN"]. Taking the first comma part made the street
 * the market, so the panel read "No comparable transactions … in 1200 Fifth
 * Avenue North, TN" and the fallback search could never match (PROP-12).
 * A bare city yields no state.
 */
export function splitLocation(location: string | null): [string | null, string | null] {
  const text = (location ?? '').trim()
  if (!text) return [null, null]
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean)
  let tail = parts[parts.length - 1].split(/\s+/)
  if (tail.length > 1 && /^\d{5}(?:-\d{4})?$/.test(tail[tail.length - 1])) tail = tail.slice(0, -1)
  let state: string | null = null
  if (/^[A-Za-z]{2}$/.test(tail[tail.length - 1] ?? '') && (parts.length >= 2 || tail.length >= 2)) {
    state = tail[tail.length - 1].toUpperCase()
    tail = tail.slice(0, -1)
  }
  let city: string | null
  if (state) city = tail.length ? tail.join(' ') : parts.length >= 2 ? parts[parts.length - 2] : null
  else city = parts.length >= 3 ? parts[parts.length - 2] : parts[0]
  if (city && LEADS_WITH_NUMBER_RE.test(city)) {
    // A street. The city is the part after it, when there is one.
    city = !state && parts.length === 2 && !LEADS_WITH_NUMBER_RE.test(parts[1]) ? parts[1] : null
  }
  return [city || null, state]
}

/** The occupancy series the deal states, else the property master's measured
 *  one. A series on the deal always wins — the master is a gap-fill, never an
 *  override of what a person or the parser put on this deal. */
export function recordedOccupancyTrend(
  raw: RawDealFile,
  masterHistory?: Record<string, unknown>[] | null,
): OccupancyPoint[] {
  const cashFlows = rawValue(raw, 'cash_flows')
  const cash = cashFlows && typeof cashFlows === 'object' && !Array.isArray(cashFlows)
    ? (cashFlows as Record<string, unknown>)
    : {}
  const source =
    rawValue(raw, 'occupancy_history') ??
    rawValue(raw, 'occupancy_trend') ??
    cash.occupancyHistory ??
    cash.occupancy_history ??
    cash.occupancyTrend ??
    (masterHistory?.length ? masterHistory : undefined)
  if (!Array.isArray(source)) return []

  return source.flatMap((entry, index) => {
    if (entry == null) return []
    const row: Record<string, unknown> = typeof entry === 'object' && !Array.isArray(entry)
      ? (entry as Record<string, unknown>)
      : { value: entry }
    const occupancy = percentNumber(row.occupancy ?? row.pct ?? row.percent ?? row.value)
    if (occupancy == null) return []
    const label = recordedText(row.period ?? row.month ?? row.date ?? row.year) ?? `#${index + 1}`
    return [{ label, occupancy: Math.max(0, Math.min(100, occupancy)) }]
  })
}

/**
 * A unit type is a name — "A1", "2x2", "1BR/1BA", "Studio" — never a bare
 * number. A schedule read off a workbook grid can carry a rent, a ratio or
 * a square-footage in its type column ("0.0784", "1016", "1"), and each of
 * those became a legend row: The Chelsea's unit mix listed thirty of them
 * and the list ran over the panels beside it.
 */
function isUnitTypeName(type: string): boolean {
  const text = type.trim()
  if (!text || text.length > 40) return false
  if (/^[-+$%.,\d\s]+$/.test(text)) return false
  return /[A-Za-z]/.test(text) || /^\d+\s*[x/]\s*\d+/.test(text)
}

const MAX_UNIT_MIX_ROWS = 8

export function recordedUnitMix(raw: RawDealFile, profile: PropertyProfile): UnitMixRow[] {
  const source = profile.unitMix?.length ? profile.unitMix : rawValue(raw, 'unit_mix')
  if (!Array.isArray(source)) return unitMixFromRentRoll(profile)
  const rows = source.flatMap((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const type = recordedText(row.type ?? row.unitType ?? row.unit_type ?? row.name ?? row.bedrooms)
    const count = numeric(row.count ?? row.units ?? row.unitCount ?? row.unit_count)
    if (!type || !isUnitTypeName(type) || count == null || count <= 0) return []
    const avgRent = numeric(row.avgRent ?? row.avg_rent ?? row.rent)
    const occupancy = percentNumber(row.occupancy)
    return [{ type, count, avgRent, occupancy }]
  })
  return rows.length ? rows : unitMixFromRentRoll(profile)
}

/**
 * A unit mix counted from the rent roll itself. A sponsor's roll states each
 * unit's floor plan; a separate unit-mix schedule is a summary of that, and
 * most packages carry the roll without the summary.
 */
function unitMixFromRentRoll(profile: PropertyProfile): UnitMixRow[] {
  const byType = new Map<string, { count: number; rent: number; rented: number; occupied: number }>()
  for (const tenant of profile.tenants ?? []) {
    const type = tenant.unitType?.trim()
    if (!type) continue
    const bucket = byType.get(type) ?? { count: 0, rent: 0, rented: 0, occupied: 0 }
    bucket.count += 1
    // The roll's status decides: a vacant line carries an asking rent.
    if (isOccupiedRow(tenant)) {
      bucket.occupied += 1
      if (tenant.rentMo > 0) {
        bucket.rent += tenant.rentMo
        bucket.rented += 1
      }
    }
    byType.set(type, bucket)
  }
  return [...byType.entries()]
    .filter(([type]) => isUnitTypeName(type))
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([type, b]) => ({
      type,
      count: b.count,
      avgRent: b.rented ? Math.round(b.rent / b.rented) : null,
      occupancy: b.count ? Math.round((b.occupied / b.count) * 1000) / 10 : null,
    }))
}

const MAX_OCCUPANCY_BARS = 12

/**
 * What the rent roll says about occupancy when no time series exists.
 *
 * A trend needs more than one date and a single roll has one, so the chart
 * had nothing to draw on every deal that carried a full rent roll beneath
 * it. The roll still answers the question a lender asks of that chart —
 * where is the vacancy — by floor plan, or by status when the roll names no
 * plans. Nothing here is a series and the panel does not call it one.
 */
export function rentRollOccupancy(profile: PropertyProfile): RentRollOccupancy | null {
  const tenants = profile.tenants ?? []
  if (tenants.length === 0) return null
  const let_ = tenants.filter(isOccupiedRow).length
  const overallPct = Math.round((let_ / tenants.length) * 1000) / 10

  const plans = unitMixFromRentRoll(profile)
  const planned = plans.reduce((sum, row) => sum + row.count, 0)
  if (plans.length >= 2 && planned >= tenants.length / 2) {
    return {
      basis: 'floor plan',
      overallPct,
      rows: plans
        .filter((row) => row.occupancy != null)
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_OCCUPANCY_BARS)
        .sort((a, b) => a.type.localeCompare(b.type, undefined, { numeric: true }))
        .map((row) => ({
          label: row.type,
          pct: row.occupancy as number,
          count: Math.round(((row.occupancy as number) / 100) * row.count),
          of: row.count,
        })),
    }
  }

  const byStatus = new Map<string, number>()
  for (const tenant of tenants) {
    const status = recordedText(tenant.status) ?? (isOccupiedRow(tenant) ? 'Occupied' : 'Vacant')
    byStatus.set(status, (byStatus.get(status) ?? 0) + 1)
  }
  return {
    basis: 'status',
    overallPct,
    rows: [...byStatus.entries()]
      .sort(([, a], [, b]) => b - a)
      .slice(0, MAX_OCCUPANCY_BARS)
      .map(([label, count]) => ({
        label,
        pct: Math.round((count / tenants.length) * 1000) / 10,
        count,
        of: tenants.length,
      })),
  }
}

export function buildLeaseExpirationRows(profile: PropertyProfile): LeaseExpirationRow[] {
  const byRent = new Map<string, number>()
  let totalRent = 0
  for (const tenant of profile.tenants ?? []) {
    const date = parseDateValue(tenant.leaseEnd)
    if (!date || tenant.rentMo <= 0) continue
    const year = String(date.getFullYear())
    byRent.set(year, (byRent.get(year) ?? 0) + tenant.rentMo)
    totalRent += tenant.rentMo
  }
  if (totalRent > 0) {
    return [...byRent.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([year, rent]) => ({
        year,
        pct: Math.round((rent / totalRent) * 1000) / 10,
        basis: 'gross rent' as const,
      }))
  }
  return profile.leaseExpirations.map((row) => ({
    year: row.year,
    pct: row.pctOfTotal,
    basis: 'area' as const,
  }))
}

export function buildRecentActivityRows(
  activity: LoanActivityEvent[],
  documents: DealDocument[],
): RecentPropertyActivity[] {
  const events: RecentPropertyActivity[] = activity.map((event) => {
    const title =
      detailText(event.details, ['title', 'filename', 'documentName', 'document_name']) ??
      humanize(event.eventType || 'Activity')
    const detail = detailText(event.details, ['message', 'detail', 'description'])
    return {
      id: `activity-${event.id}`,
      at: event.createdAt,
      title,
      actor: event.userEmail,
      detail: detail === title ? null : detail,
      kind: 'activity' as const,
    }
  })
  const files: RecentPropertyActivity[] = documents.map((document) => ({
    id: `document-${document.id}`,
    at: document.createdAt,
    title: document.filename,
    // An intake original had no uploader; it arrived with the package.
    actor: receivedByEmail(document) ? 'Received by email' : document.uploadedBy,
    detail: recordedText(document.category),
    kind: 'document' as const,
  }))
  return [...events, ...files]
    .sort((left, right) => Date.parse(right.at ?? '') - Date.parse(left.at ?? ''))
    .slice(0, 6)
}

function extraText(comp: { sourceExtras?: DossierComparable['sourceExtras'] }, ...headings: string[]): string | null {
  const extras = comp.sourceExtras ?? {}
  for (const heading of headings) {
    const match = Object.entries(extras).find(([key]) => key === heading || key.includes(heading))
    const value = match?.[1]?.value
    if (value !== undefined && value !== null && value !== '') return String(value)
  }
  return null
}

/** A money column from the sponsor's extras, as money when it is a number. */
function extraMoney(comp: { sourceExtras?: DossierComparable['sourceExtras'] }, ...headings: string[]): string | null {
  const text = extraText(comp, ...headings)
  if (text == null) return null
  const n = Number(text)
  return Number.isFinite(n) ? formatMoney(n) : text
}

function MetricTile({ icon: Icon, label, value, compact = false }: { icon: LucideIcon; label: string; value: ReactNode; compact?: boolean }) {
  return (
    <div className={`flex min-w-0 items-center gap-2 rounded-md border border-amber-400/10 bg-black/40 ${compact ? 'min-h-[36px] px-2 py-1' : 'px-2.5 py-2'}`}>
      <span className={`flex shrink-0 items-center justify-center rounded border border-amber-300/10 bg-amber-300/[0.04] text-amber-300/70 ${compact ? 'h-6 w-6' : 'h-7 w-7'}`}>
        <Icon size={compact ? 11 : 13} />
      </span>
      <div className="min-w-0">
        <div className="truncate font-mono text-[8px] uppercase tracking-widest text-stone-600">{label}</div>
        <div className={`truncate font-mono font-semibold text-stone-100 ${compact ? 'text-[12px]' : 'text-[13px]'}`}>{value}</div>
      </div>
    </div>
  )
}

/**
 * How many rows of a given height fit in an element, measured live. The
 * dashboard slots are sized by the viewport, not by their content, so a
 * table that must not scroll shows the rows the slot has room for and
 * says how many more there are.
 */
function useRowsThatFit(rowHeight: number, reserve: number) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [count, setCount] = useState(3)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const room = el.clientHeight - reserve
      setCount(Math.max(1, Math.floor(room / rowHeight)))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [rowHeight, reserve])
  return { ref, count }
}

/**
 * Where the photos come from, said out loud when there are none.
 *
 * Three sources feed this panel: photos attached to the deal, Google Places and
 * Street View for the address, and the property's own website. When all three
 * come back empty the panel used to say "No property photos are attached to
 * this deal" — which names none of them, and left a reader with no way to tell
 * a property with no photos from a Maps key nobody had entered.
 *
 * The lookup already explains itself; those notes were suppressed in compact
 * mode. They are the answer to "why is this empty", so they are shown, and the
 * one source a person can supply from here — the property's website — is
 * offered beside them.
 */
export function PropertyPhotosPanel(
  props: Pick<Props, 'photos' | 'mediaNotes' | 'mediaLoading' | 'formattedAddress' | 'website' | 'onLinkPhotoSource'>
    & Partial<Pick<Props, 'mapBasis' | 'cityLat' | 'cityLng' | 'addressParts' | 'profile' | 'mapsAerialEnabled'>>,
) {
  const [draft, setDraft] = useState('')
  // A deal that states only its city has no building to photograph. What can be
  // shown truthfully is the city itself, from the air, and labelled as such.
  const area = props.profile ? cityArea({ ...props, profile: props.profile }) : null
  const areaUrl = area && props.mapsAerialEnabled === true ? aerialSnapshotUrl(area.lat, area.lng) : null
  const [areaFailed, setAreaFailed] = useState(false)
  useEffect(() => setAreaFailed(false), [areaUrl])
  const { unlocked } = useDossierEdit()
  const notes = props.mediaNotes ?? []
  // The one source a person can supply here, the property's website, is
  // offered in the panel's header line while the dossier is unlocked. Drawn
  // above the gallery it pushed the gallery down inside a panel that does not
  // scroll, clipping "No photos found" under a padded box: unlocking must move
  // nothing, and only the input carries the edit-mode gold hairline.
  const linkForm = unlocked && props.onLinkPhotoSource ? (
    <form
      className="flex min-w-0 items-center gap-2 font-mono text-[9px] leading-tight text-stone-200"
      onSubmit={event => { event.preventDefault(); const url = draft.trim(); if (url) props.onLinkPhotoSource?.(url) }}
    >
      <input
        type="url"
        required
        value={draft}
        onChange={event => setDraft(event.target.value)}
        placeholder={props.website || 'Property website'}
        aria-label="Property website to pull photos from"
        className="dossier-edit-input"
        style={{ width: '24ch' }}
      />
      <button type="submit" className="shrink-0 text-[10px] text-amber-300 hover:text-amber-200">Link</button>
    </form>
  ) : undefined

  return (
    <DossierPanel title="Property photos" scroll={false} dense className="h-full min-h-[200px] xl:min-h-0" headerExtra={linkForm}>
      <div className="flex h-full min-h-0 flex-col gap-2 overflow-hidden rounded-md">
        <div className="min-h-0 flex-1">
        <MediaGallery
          photos={props.photos}
          notes={notes}
          loading={Boolean(props.mediaLoading)}
          formattedAddress={props.formattedAddress ?? null}
          compact
          chromeless
          emptyFallback={areaUrl && !areaFailed ? (
            <figure className="relative m-0 h-full min-h-[120px] overflow-hidden rounded-md" data-testid="property-photos-area-view">
              <img
                src={areaUrl}
                alt={`Aerial view of ${area?.label ?? 'the city'}, city center`}
                className="absolute inset-0 h-full w-full object-cover"
                onError={() => setAreaFailed(true)}
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2 pb-1.5 pt-5">
                <span className="block font-mono text-[9px] uppercase tracking-wider text-amber-200">
                  Area view · {area?.label}
                </span>
                <span className="block text-[9px] leading-snug text-stone-300">
                  City center, not the property: no street address is on record. {AERIAL_IMAGERY.attribution}.
                </span>
              </figcaption>
            </figure>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-5 text-center">
              <p className="text-[11px] text-stone-500">
                No photos found for this property.
              </p>
              {notes.length > 0 ? (
                <ul className="space-y-0.5">
                  {notes.map((note) => (
                    <li key={note} className="text-[9px] leading-relaxed text-stone-600">{note}</li>
                  ))}
                </ul>
              ) : null}

            </div>
          )}
        />
        </div>
      </div>
    </DossierPanel>
  )
}

/** A street address starts with a number; "Norcross, GA" does not. */
const STREET_NUMBER_RE = /^\s*\d/

/**
 * Property-group catalogue facts the summary grid does not already show.
 *
 * Intake captured every one of these — The Ardsley's brand and management,
 * Vireo's zoning and MSA, Calloway's stated building area — and the tab showed
 * none of them; they were reachable only under Audit > All fields
 * (stress test PROP-09). A fact nobody stated is not printed as a blank row in
 * the dashboard slot; the full-size panel and edit mode list every one.
 */
const COLLATERAL_FACTS: { name: string; label: string }[] = [
  { name: 'property_subtype', label: 'Subtype' },
  { name: 'property_brand', label: 'Brand / flag' },
  { name: 'property_management', label: 'Management' },
  { name: 'tenure', label: 'Ownership interest' },
  { name: 'zoning', label: 'Zoning' },
  { name: 'msa', label: 'MSA' },
  { name: 'building_area', label: 'Building area (as stated)' },
]

/** The facts whose cell ends below the bottom of `box`: laid out, out of sight. */
function useFactsBelowFold(box: RefObject<HTMLElement | null>, active: boolean, layoutKey: string): string[] {
  const [folded, setFolded] = useState<string[]>([])
  useLayoutEffect(() => {
    const el = box.current
    if (!active || !el) {
      setFolded((prev) => (prev.length ? [] : prev))
      return
    }
    const measure = () => {
      const limit = el.getBoundingClientRect().bottom + 0.5
      const next = Array.from(el.querySelectorAll<HTMLElement>('[data-fact]'))
        .filter((cell) => cell.getBoundingClientRect().bottom > limit)
        .map((cell) => cell.dataset.fact ?? '')
      setFolded((prev) => (prev.join('|') === next.join('|') ? prev : next))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [box, active, layoutKey])
  return folded
}

type SummaryFact = { key: string; label: string; recorded: boolean; wide?: boolean; node: ReactNode }

/** Facts the rent-roll / hotel-operations panel under the summary carries as tiles. */
const SHOWN_BELOW = new Set(['units', 'occupancy'])

function PropertySummaryPanel(props: {
  profile: PropertyProfile
  addressStatus?: AssetPointStatus
  addressUnchecked?: boolean
  addressPending?: boolean
  addressStateOnly?: boolean
  addressEditable?: boolean
  onGoto: () => void
}) {
  return (
    <DossierPanel title="Property summary" dense scroll={false} className="h-full min-h-[200px] xl:min-h-0">
      {(ctx) => <PropertySummaryBody {...props} expanded={ctx.expanded} onExpand={ctx.expand} />}
    </DossierPanel>
  )
}

function PropertySummaryBody({
  profile,
  addressStatus,
  addressUnchecked,
  addressPending,
  addressStateOnly,
  addressEditable = false,
  expanded,
  onExpand,
}: {
  profile: PropertyProfile
  addressStatus?: AssetPointStatus
  addressUnchecked?: boolean
  addressPending?: boolean
  addressStateOnly?: boolean
  addressEditable?: boolean
  expanded: boolean
  onExpand: () => void
}) {
  const { effectiveValue, unlocked } = useDossierEdit()
  const address = recordedText(profile.fullAddress)
  const unverified = addressStatus === 'unverified'
  // The page knows whether a street is on record (`addressStatus`); only a
  // caller that never said falls back to reading the text, and then a street
  // need not open with a plain number ("One Liberty Plaza").
  const cityOnly =
    !unverified && (addressStatus ? addressStatus === 'city' : address != null && !STREET_NUMBER_RE.test(address))
  const slot = (name: string, label: string, display: string, kind: SlotKind): SummaryFact => ({
    key: name,
    label,
    recorded: display !== '—' || recordedText(effectiveValue(name)) != null,
    node: <EditableSlot name={name} label={label} display={display} kind={kind} layout="stack" />,
  })
  const county = recordedText(profile.county)
  const description = recordedText(effectiveValue('property_description'))
  const unitsLabel = isLodging(profile.propertyType) ? keysLabel(effectiveValue('unit_of_measure')) : profile.unitsFromRentRoll ? 'Units · rent roll' : profile.unitCountVariance ? `Units · roll lists ${profile.unitCountVariance.rentRoll}` : 'Units'
  // The county the deal states is its own field and edits as one; a county
  // only the public-record lookup resolved (Census) is that source's answer,
  // not a statement on the deal, and an input over it would show an empty box
  // where the county was.
  const dealCounty = recordedText(effectiveValue('county'))
  const facts: SummaryFact[] = [
    dealCounty != null
      ? slot('county', 'County', dealCounty, 'text')
      : {
          key: 'county',
          label: 'County',
          recorded: county != null,
          node: (
            <StackedField
              label="County"
              value={county ?? '—'}
              title={unlocked && county != null ? 'From a public source (Census), not a figure stated on the deal.' : undefined}
            />
          ),
        },
    slot('parcel_id', 'Parcel ID', recordedText(profile.parcel) ?? '—', 'text'),
    slot('year_built', 'Year built', profile.yearBuilt ? String(profile.yearBuilt) : '—', 'text'),
    slot('year_renovated', 'Year renovated', profile.yearRenovated ? String(profile.yearRenovated) : '—', 'text'),
    // `sf` holds whatever area the package stated — gross, rentable or
    // net all bind here — so it is not labelled as any one of them.
    // Calloway's "274080" under "Gross building area" was its NRSF.
    slot('sf', 'Square feet (as stated)', recordedText(profile.totalSF) ?? '—', 'count'),
    slot('site_area', 'Lot size', recordedText(profile.lotSize) ?? '—', 'text'),
    slot('stories', 'Stories', profile.stories ? String(profile.stories) : '—', 'text'),
    slot('units', unitsLabel, profile.totalUnits ? String(profile.totalUnits) : '—', 'count'),
    slot('occupancy', 'Occupancy', recordedText(profile.occupancy) ?? '—', 'percent'),
    ...COLLATERAL_FACTS.map(({ name, label }) => slot(name, label, recordedText(effectiveValue(name)) ?? '—', 'text')),
    {
      key: 'property_description',
      label: 'Description',
      recorded: description != null,
      wide: true,
      node: unlocked ? (
        <EditableSlot name="property_description" label="Description" display={description ?? '—'} kind="text" layout="stack" />
      ) : (
        <StackedField
          label="Description"
          value={<span className={expanded ? undefined : 'line-clamp-3'}>{description ?? '—'}</span>}
        />
      ),
    },
  ]
  // The dashboard slot is a fixed height. A fact nobody stated is not printed
  // there as a blank row, nor one the rent-roll or hotel panel directly below
  // already shows — the full-size panel and edit mode list every one — and a
  // fact that does not fit whole is hidden and named in the link below, never
  // cut off mid-text under an unmarked scroll (PROP-09 recheck).
  const dashboard = !expanded && !unlocked
  const shown = dashboard
    ? facts.filter((fact) => fact.recorded && !SHOWN_BELOW.has(fact.key))
    : facts
  const boxRef = useRef<HTMLDivElement>(null)
  const folded = useFactsBelowFold(boxRef, dashboard, shown.map((fact) => fact.key).join('|'))
  const foldedLabels = shown.filter((fact) => folded.includes(fact.key)).map((fact) => fact.label)
  return (
    <div className={expanded ? undefined : 'flex h-full min-h-0 flex-col'}>
      <div
        ref={boxRef}
        data-testid="summary-facts"
        // Unlocked, each input's hairline sits 3px outside its column (the
        // edit style's negative margin), which gave this scroll box a 3px
        // sideways scroll at both 1366 and 1600; the box makes room for it.
        className={expanded ? undefined : `min-h-0 flex-1 ${unlocked ? 'overflow-y-auto overflow-x-hidden px-[3px]' : 'overflow-hidden'}`}
      >
        <div className="break-words text-[11px] leading-relaxed text-stone-300">
          {addressPending ? (
            <span data-testid="address-pending" className="text-stone-500">{ADDRESS_PENDING_TEXT}</span>
          ) : (
            // Unlocked, the address opens its street, city, state and postal
            // code — the fields it is stated in; `location` is recomposed
            // from them by the server on save, so it is never typed itself.
            // Only a street something vouches for is opened: under "Address
            // not stated in package" the deal file may still hold a Places
            // name lookup's street and city, and the boxes showed that guess
            // pre-filled as the deal's street (the header never did).
            <EditableValue
              name="street"
              label="Property address"
              fields={addressEditable && !unverified ? ADDRESS_EDIT_TARGETS : []}
              readOnlyReason={ADDRESS_CONFIRM_REASON}
              popover
              display={unverified ? (addressUnchecked ? UNCHECKED_ADDRESS : 'Address not stated in package') : address ?? NOT_ON_RECORD}
            />
          )}
        </div>
        {addressPending ? null : addressStateOnly && !unverified ? (
          <p className="mt-1 text-[9px] leading-snug text-amber-300/80" data-testid="address-state-only">
            {STATE_ONLY_NOTE} Confirm the street address from the header.
          </p>
        ) : unverified && addressUnchecked ? (
          <p className="mt-1 text-[9px] leading-snug text-amber-300/80" data-testid="address-unchecked-note">
            Where each part of this address came from could not be loaded, so the deal file&apos;s
            address is not shown and no map, photos or parcel are looked up. Reload to try again.
          </p>
        ) : unverified ? (
          <p className="mt-1 text-[9px] leading-snug text-amber-300/80" data-testid="address-unverified">
            No document or person supports an address for this deal, so the map, photos and
            parcel are not shown. Confirm the street address from the header.
          </p>
        ) : cityOnly ? (
          <p className="mt-1 text-[9px] leading-snug text-amber-300/80" data-testid="address-city-only">
            City-level only — no street address is on record, so the map, photos and parcel
            cannot locate the building. Add the street.
          </p>
        ) : null}
        {/* Two columns of label-over-value cells. The single column of
            118px-label rows clipped every value longer than a year — the
            building area and lot size were cut off mid-figure. */}
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
          {shown.map((fact) => {
            const hidden = folded.includes(fact.key)
            return (
              <div
                key={fact.key}
                data-fact={fact.key}
                data-testid={fact.key === 'property_description' ? 'property-description' : undefined}
                aria-hidden={hidden ? true : undefined}
                className={`min-w-0 ${fact.wide ? 'col-span-2' : ''} ${hidden ? 'invisible' : ''}`}
              >
                {fact.node}
              </div>
            )
          })}
        </div>
        {/* Blank facts are left off the dashboard slot; when that leaves
            nothing, say so — an empty panel reads as a load failure. */}
        {shown.length === 0 ? (
          <p className="mt-2 text-[10px] leading-snug text-stone-500">
            No property facts are on record — the full panel lists every field.
          </p>
        ) : null}
      </div>
      {foldedLabels.length > 0 ? (
        <button
          type="button"
          onClick={onExpand}
          title={foldedLabels.join(', ')}
          className="mt-1 shrink-0 truncate border-t border-white/[0.06] pt-1 text-left text-[10px] text-amber-300 hover:text-amber-200"
        >
          +{foldedLabels.length} more: {foldedLabels.join(', ')} · View all
        </button>
      ) : null}
    </div>
  )
}

/** Why the Location panel shows no map: an address nothing supports. */
const UNVERIFIED_MAP_REASON = 'Address not stated in package'
/** …or one whose provenance could not be read, so nothing can vouch for it. */
const UNCHECKED_ADDRESS = 'Address could not be checked'

function LocationPanel(props: Pick<Props, 'address' | 'mapsKey' | 'mapsConfigured' | 'mapsError' | 'mapsLoading' | 'mapsAerialEnabled' | 'mapLat' | 'mapLng' | 'mapBasis' | 'cityLat' | 'cityLng' | 'siteIntel' | 'addressUnchecked'> & { locating: boolean }) {
  // Absent means the caller predates the distinction: a building coordinate.
  const status = props.mapBasis ?? 'asset'
  const blocked = status === 'unverified'
  const basis = status === 'city' ? 'city' : 'asset'
  // The parcel and footprint are for the building. A city, or an address
  // nothing supports, is not one, whatever the site lookup happened to hold.
  const siteLevel = basis === 'asset' && !blocked
  // One place decides how an address becomes a link, so every address on the
  // platform opens the same map. This built its own URL and, unlike the shared
  // helper, treated 0,0 — what an un-geocoded record looks like once null
  // becomes a number — as a location in the Gulf of Guinea. An address nothing
  // supports opens no map at all.
  const openMapUrl = blocked
    ? null
    : mapsUrl({ address: props.address, lat: props.mapLat, lng: props.mapLng })
  return (
    <DossierPanel
      title="Location"
      scroll={false}
      action={openMapUrl ? 'Open map' : undefined}
      onAction={openMapUrl ? () => window.open(openMapUrl, '_blank', 'noopener') : undefined}
      dense
      className="h-full min-h-[200px] xl:min-h-0"
    >
      <div className="h-full min-h-0 overflow-hidden rounded-md">
        <PropertyMap
          compact
          chromeless
          address={props.address}
          apiKey={props.mapsKey}
          keyConfigured={props.mapsConfigured}
          keyError={props.mapsError}
          keyLoading={props.mapsLoading}
          aerialEnabled={props.mapsAerialEnabled}
          lat={basis === 'city' ? props.cityLat : props.mapLat}
          lng={basis === 'city' ? props.cityLng : props.mapLng}
          basis={basis}
          blockedReason={blocked ? (props.addressUnchecked ? UNCHECKED_ADDRESS : UNVERIFIED_MAP_REASON) : null}
          locating={props.locating}
          parcel={siteLevel ? props.siteIntel?.parcelGeometry ?? null : null}
          footprint={siteLevel ? props.siteIntel?.buildingFootprint ?? null : null}
        />
      </div>
    </DossierPanel>
  )
}

/**
 * The property's own figures in one panel. The two cap rates sat in a "Property
 * budget" panel of their own above this one; they lead it now (Edward, 30 Oct
 * 2026), and the column's height went to Comparables.
 */
function KeyMetricsPanel({ profile, onGoto }: { profile: PropertyProfile; onGoto: () => void }) {
  const { effectiveValue } = useDossierEdit()
  const lodging = isLodging(profile.propertyType)
  const parking = recordedText(effectiveValue('parking_spaces')) ?? recordedText(profile.parking) ?? '—'
  const walt = recordedText(effectiveValue('avg_lease_term')) ?? recordedText(profile.avgLeaseTerm) ?? '—'
  const stabilizedCap = effectiveValue('cap_rate_stabilized') ?? profile.stabilizedCapRate
  const stabilizedCapDisplay = recordedText(stabilizedCap)?.includes('%')
    ? String(stabilizedCap)
    : formatPct(stabilizedCap)
  // The slot shows the STORED figure, and the engine stored NOI ÷ value even
  // when one of the two was wrong: The Chelsea's $300,000 value against a
  // -$1.95M NOI stood here as a cap rate of -65050.43%. A figure outside the
  // column's own 0-100 band is not shown as one: the tile reads "n/m" (not
  // meaningful) and says why on hover.
  const storedCap = numeric(effectiveValue('cap_rate'))
  const capOffScale = storedCap != null && storedCap !== 0 && plausibleCapRate(storedCap) == null
  const offScaleNote = capOffScale
    ? `NOI ÷ value computes to ${formatPct(storedCap)} — not a cap rate. The NOI or the value recorded on this deal is wrong; correct it on Financials.`
    : undefined
  return (
    <DossierPanel title="Key metrics" scroll={false} action="Financials" onAction={onGoto} dense className="h-full min-h-0">
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
        {capOffScale ? (
          <div title={offScaleNote} data-testid="cap-rate-off-scale">
            {/* Unlocked, "n/m" opens the NOI and the value it was divided
                from: the figure is wrong because one of them is, and that is
                where it is corrected. */}
            <MetricTile
              icon={Gauge}
              label="Cap rate"
              value={<EditableValue name="cap_rate" label="Cap rate" display="n/m" computed="NOI ÷ value" inputs={['noi', 'property_value']} />}
              compact
            />
          </div>
        ) : (
          <EditableSlot name="cap_rate" label="Cap rate" display={profile.capRate} kind="percent" layout="tile" icon={Gauge} compact />
        )}
        <EditableSlot name="cap_rate_stabilized" label="Stabilized cap rate" display={stabilizedCapDisplay} kind="percent" layout="tile" icon={Gauge} compact />
        <EditableSlot name="dscr" label="DSCR (in-place)" display={formatMultiple(effectiveValue('dscr'))} kind="number" layout="tile" icon={Gauge} compact />
        <EditableSlot name="dscr_stabilized" label="DSCR (stabilized)" display={formatMultiple(effectiveValue('dscr_stabilized'))} kind="number" layout="tile" icon={Gauge} compact />
        <EditableSlot name="breakeven_occupancy" label="Breakeven occ." display={formatPct(effectiveValue('breakeven_occupancy'))} kind="percent" layout="tile" icon={TrendingUp} compact />
        {/* Avg unit size is computed (SF ÷ units), not a stored field — no
            write path exists to invent here. Unlocked, it opens the two
            figures it is made of. */}
        <MetricTile
          icon={Home}
          label="Avg unit size"
          value={
            <EditableValue
              name="avg_unit_size"
              label="Avg unit size"
              display={recordedText(profile.avgUnitSize) ?? '—'}
              computed="square feet ÷ units"
              inputs={['sf', 'units']}
            />
          }
          compact
        />
        <EditableSlot name="parking_spaces" label="Parking spaces" display={parking} kind="count" layout="tile" icon={ParkingSquare} compact />
        {/* A hotel has no leases, so no weighted average lease term. */}
        {lodging ? null : <EditableSlot name="avg_lease_term" label="WALT" display={walt} kind="text" layout="tile" icon={CalendarClock} compact />}
      </div>
    </DossierPanel>
  )
}

const RENT_ROLL_ROW_PX = 17

function OccupancyLine({ trend }: { trend: OccupancyPoint[] }) {
  return (
    <div className="min-h-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 2, left: -14 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: STONE, fontSize: 8 }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fill: STONE, fontSize: 8 }} axisLine={false} tickLine={false} tickFormatter={(value) => `${value}%`} />
          <Tooltip
            contentStyle={{ background: '#0a0a0a', border: '1px solid rgba(217,183,104,0.2)', fontSize: 10 }}
            formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Occupancy']}
          />
          <Line type="monotone" dataKey="occupancy" stroke={GOLD} strokeWidth={2} dot={{ r: 2, fill: GOLD }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

/**
 * A hotel's operating figures, where an apartment's rent roll would sit.
 *
 * ADR, RevPAR and room nights were captured on every hotel intake and shown
 * only under Audit, while this slot offered a rent roll, WALT and rent per SF
 * that a hotel can never fill (stress test PROP-16).
 */
function HotelOperationsPanel({
  raw,
  profile,
  occupancyHistory,
}: {
  raw: RawDealFile
  profile: PropertyProfile
  occupancyHistory?: Record<string, unknown>[] | null
}) {
  const { effectiveValue } = useDossierEdit()
  const trend = useMemo(
    () => recordedOccupancyTrend(raw, occupancyHistory),
    [raw, occupancyHistory],
  )
  return (
    <DossierPanel title="Hotel operations" scroll={false} dense className="h-full min-h-[220px] xl:min-h-0">
      {({ expanded }) => (
        <div className="flex h-full min-h-0 flex-col">
          <div className="grid shrink-0 grid-cols-2 gap-1 sm:grid-cols-3 xl:grid-cols-5">
            <EditableSlot name="units" label={keysLabel(effectiveValue('unit_of_measure'))} display={profile.totalUnits ? profile.totalUnits.toLocaleString('en-US') : '—'} kind="count" layout="tile" icon={Users} compact={!expanded} />
            <EditableSlot name="occupancy" label="Occupancy" display={recordedText(profile.occupancy) ?? '—'} kind="percent" layout="tile" icon={Gauge} compact={!expanded} />
            <EditableSlot name="adr" label="ADR" display={formatAmount(effectiveValue('adr'))} kind="currency" layout="tile" icon={DollarSign} compact={!expanded} />
            <EditableSlot name="revpar" label="RevPAR" display={formatAmount(effectiveValue('revpar'))} kind="currency" layout="tile" icon={TrendingUp} compact={!expanded} />
            <EditableSlot name="room_nights" label="Room nights" display="—" kind="count" layout="tile" icon={CalendarClock} compact={!expanded} />
          </div>
          <div className="mt-1.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-white/[0.04] bg-black/20 p-1.5">
            <div className="shrink-0 font-mono text-[8px] uppercase tracking-widest text-stone-600">Occupancy trend</div>
            {trend.length < 2 ? (
              <EmptyLine>No recorded occupancy time series.</EmptyLine>
            ) : (
              <OccupancyLine trend={trend} />
            )}
          </div>
          <p className="shrink-0 pt-1 text-[9px] text-stone-600">
            A hotel is let by the night: no rent roll, lease schedule or WALT applies.
          </p>
        </div>
      )}
    </DossierPanel>
  )
}

function RentRollSummaryPanel({
  raw,
  profile,
  occupancyHistory,
}: {
  raw: RawDealFile
  profile: PropertyProfile
  occupancyHistory?: Record<string, unknown>[] | null
}) {
  const { effectiveValue } = useDossierEdit()
  const trend = useMemo(
    () => recordedOccupancyTrend(raw, occupancyHistory),
    [raw, occupancyHistory],
  )
  const unitMix = useMemo(() => recordedUnitMix(raw, profile), [raw, profile])
  const totalMixUnits = unitMix.reduce((sum, row) => sum + row.count, 0)
  const tenants = profile.tenants ?? []
  // A single-tenant sale-leaseback has no rent roll to compute "In-place
  // rent/mo" or "Avg rent / SF" from — both tiles read "—" even when the
  // lease's own headline rent is stated (owner decision #7, round 6:
  // Cranbury's "Current Base Rent $1,368,000 / yr ($5.70/SF)"). Those two
  // are real catalogue fields now; show them here, in the rent roll's own
  // slots, in place of the computed tile — but ONLY when the field is
  // actually stated. A deal with neither a rent roll nor a stated headline
  // rent (an ordinary apartment deal with no per-unit schedule on file)
  // keeps reading exactly as it did before this field existed.
  const hasRentRoll = tenants.length > 0
  const isPresentValue = (v: unknown) => v != null && v !== ''
  const showStatedBaseRent = !hasRentRoll && isPresentValue(effectiveValue('base_rent_annual'))
  const showStatedRentPsf = !hasRentRoll && isPresentValue(effectiveValue('rent_per_sf'))
  const occupiedCount = tenants.filter(isOccupiedRow).length
  const rollOccupancy = useMemo(() => rentRollOccupancy(profile), [profile])
  const showLeaseStart = tenants.some((t) => t.leaseStart && t.leaseStart !== '—')
  const showTerm = tenants.some((t) => t.termMonths != null)
  const showPsf = tenants.some((t) => t.rentPsf != null)
  const showRehab = tenants.some((t) => Boolean(t.rehab))
  // In the dashboard slot the table shows the rows it has room for (the
  // header and the "+N more" line reserved); at full size, the whole roll.
  const fit = useRowsThatFit(RENT_ROLL_ROW_PX, 30)
  const dashboardRows = Math.max(1, fit.count)
  return (
    <DossierPanel title="Rent roll summary" scroll={false} dense className="h-full min-h-[220px] xl:min-h-0">
      {({ expanded }) => (
      <div className="flex h-full min-h-0 flex-col">
        <div className="grid shrink-0 grid-cols-2 gap-1 sm:grid-cols-3 xl:grid-cols-5">
          <EditableSlot name="units" label={profile.unitsFromRentRoll ? 'Units · rent roll' : 'Total units'} display={profile.totalUnits ? String(profile.totalUnits) : '—'} kind="count" layout="tile" icon={Users} compact={!expanded} />
          <EditableSlot name="occupancy" label="Occupancy" display={recordedText(profile.occupancy) ?? '—'} kind="percent" layout="tile" icon={Gauge} compact={!expanded} />
          {/* Monthly rent and avg rent/SF are computed from the LEASED units of the
              rent roll below, not a single stored field — editable on each
              tenant row instead. Where there is no roll at all (a
              single-tenant lease), the lease's own stated headline rent
              fills the same two slots instead of reading "—". */}
          {showStatedBaseRent ? (
            <EditableSlot name="base_rent_annual" label="Base rent (annual)" display={formatAmount(effectiveValue('base_rent_annual'))} kind="currency" layout="tile" icon={DollarSign} compact={!expanded} />
          ) : (
            <MetricTile
              icon={DollarSign}
              label="In-place rent/mo"
              value={
                <EditableValue
                  name="in_place_rent_monthly"
                  label="In-place rent/mo"
                  display={profile.monthlyRentTotal ? formatMoney(profile.monthlyRentTotal) : '—'}
                  computed={profile.monthlyRentTotal ? 'the leased units of the rent roll below' : undefined}
                  inputs={[]}
                  readOnlyReason="Computed from the leased units of the rent roll below — not a stored field."
                />
              }
              compact={!expanded}
            />
          )}
          {showStatedRentPsf ? (
            <EditableSlot name="rent_per_sf" label="Rent / SF" display={formatAmount(effectiveValue('rent_per_sf'))} kind="currency" layout="tile" icon={Building2} compact={!expanded} />
          ) : (
            <MetricTile
              icon={Building2}
              label="Avg rent / SF"
              value={
                <EditableValue
                  name="avg_rent_per_sf"
                  label="Avg rent / SF"
                  display={recordedText(profile.avgRentPerSF) ?? '—'}
                  computed={recordedText(profile.avgRentPerSF) ? 'the leased units of the rent roll below' : undefined}
                  inputs={[]}
                  readOnlyReason="Computed from the leased units of the rent roll below — not a stored field."
                />
              }
              compact={!expanded}
            />
          )}
          <EditableSlot name="avg_lease_term" label="WALT" display={recordedText(profile.avgLeaseTerm) ?? '—'} kind="text" layout="tile" icon={CalendarClock} compact={!expanded} />
        </div>
        {profile.unitCountVariance ? (
          <p className="mt-1 shrink-0 text-[9px] text-amber-300/90" data-testid="unit-count-variance">
            Stated {profile.unitCountVariance.stated.toLocaleString()} units · rent roll lists{' '}
            {profile.unitCountVariance.rentRoll.toLocaleString()} — confirm the count; per-unit figures are withheld until it is settled.
          </p>
        ) : null}
        {/* A fixed band, not a flex share: with the rent roll below taking
            the rest, two flex-1 regions fought for the height and the unit
            mix legend ran over the tiles and the table beside it. */}
        <div className={`mt-1.5 grid shrink-0 gap-2 overflow-hidden lg:grid-cols-[minmax(0,1.35fr)_minmax(200px,0.65fr)] ${expanded ? 'h-[220px]' : 'h-[96px]'}`}>
          <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-white/[0.04] bg-black/20 p-1.5">
            <div className="flex shrink-0 items-baseline justify-between gap-2 font-mono text-[8px] uppercase tracking-widest text-stone-600">
              <span>{trend.length >= 2 || !rollOccupancy ? 'Occupancy trend' : `Occupancy by ${rollOccupancy.basis}`}</span>
              {trend.length < 2 && rollOccupancy ? (
                <span className="truncate normal-case tracking-normal" data-testid="occupancy-basis-note">
                  Rent roll · {rollOccupancy.overallPct.toFixed(1)}% let · no time series on record
                </span>
              ) : null}
            </div>
            {trend.length < 2 && rollOccupancy && rollOccupancy.rows.length > 0 ? (
              <div className="min-h-0 flex-1" data-testid="rent-roll-occupancy-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rollOccupancy.rows} margin={{ top: 10, right: 8, bottom: 0, left: -14 }} barCategoryGap="22%">
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                    <XAxis dataKey="label" interval={0} tick={{ fill: STONE, fontSize: 8 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={{ fill: STONE, fontSize: 8 }} axisLine={false} tickLine={false} tickFormatter={(value) => `${value}%`} />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.025)' }}
                      contentStyle={{ background: '#0a0a0a', border: '1px solid rgba(217,183,104,0.2)', fontSize: 10 }}
                      formatter={(value, _name, item) => {
                        const row = item?.payload as RentRollOccupancy['rows'][number] | undefined
                        return [
                          `${Number(value).toFixed(1)}%${row ? ` · ${row.count} of ${row.of}` : ''}`,
                          rollOccupancy.basis === 'floor plan' ? 'Let' : 'Share of roll',
                        ]
                      }}
                    />
                    {rollOccupancy.basis === 'floor plan' ? (
                      <ReferenceLine y={rollOccupancy.overallPct} stroke="#a8a29e" strokeDasharray="3 3" strokeWidth={1} />
                    ) : null}
                    <Bar dataKey="pct" fill={GOLD} radius={[2, 2, 0, 0]} maxBarSize={38} isAnimationActive={false}>
                      <LabelList dataKey="pct" position="top" fill="#a8a29e" fontSize={8} formatter={(value: unknown) => `${Number(value).toFixed(0)}%`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : trend.length < 2 ? (
              <EmptyLine>No recorded occupancy time series, and no rent roll to read occupancy from.</EmptyLine>
            ) : (
              <OccupancyLine trend={trend} />
            )}
          </div>
          <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-white/[0.04] bg-black/20 p-1.5">
            <div className="shrink-0 font-mono text-[8px] uppercase tracking-widest text-stone-600">Unit mix</div>
            {unitMix.length === 0 ? (
              <EmptyLine>No recorded unit-mix schedule.</EmptyLine>
            ) : (
              <div className="grid min-h-0 flex-1 grid-cols-[88px_minmax(0,1fr)] items-center gap-2">
                <div className="relative h-[76px] w-[76px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={unitMix} dataKey="count" nameKey="type" innerRadius="62%" outerRadius="88%" paddingAngle={1} stroke="transparent" isAnimationActive={false}>
                        {unitMix.map((row, index) => <Cell key={row.type} fill={UNIT_COLORS[index % UNIT_COLORS.length]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-mono text-[13px] text-stone-100">{totalMixUnits}</span>
                    <span className="text-[8px] uppercase tracking-wider text-stone-500">Units</span>
                  </div>
                </div>
                <ul className="max-h-full min-h-0 space-y-0.5 overflow-hidden">
                  {unitMix.slice(0, MAX_UNIT_MIX_ROWS).map((row, index) => (
                    <li key={row.type} className="flex items-center gap-1.5 text-[9px] text-stone-400">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: UNIT_COLORS[index % UNIT_COLORS.length] }} />
                      <span className="min-w-0 flex-1 truncate">{row.type}</span>
                      <span className="font-mono text-stone-300">{row.count}</span>
                    </li>
                  ))}
                  {unitMix.length > MAX_UNIT_MIX_ROWS ? (
                    <li className="text-[8px] text-stone-600">+{unitMix.length - MAX_UNIT_MIX_ROWS} more types</li>
                  ) : null}
                </ul>
              </div>
            )}
          </div>
        </div>
        {/* Below xl the dashboard scrolls as a page and a 317-unit roll would
            be a ten-thousand-pixel column; the table scrolls inside a cap
            there, and takes the panel's remaining height on the desktop grid. */}
        {/* In the dashboard slot the table shows the rows that fit and says
            how many more there are; at full size it is the whole roll. */}
        <div ref={expanded ? undefined : fit.ref} className="mt-1.5 flex min-h-[48px] min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-white/[0.04] bg-black/20 p-1.5" data-testid="rent-roll-rows">
          <div className="flex shrink-0 items-baseline justify-between gap-2">
            <span className="font-mono text-[8px] uppercase tracking-widest text-stone-600">Rent roll</span>
            {tenants.length > 0 ? (
              <span className="font-mono text-[8px] text-stone-600">
                {tenants.length} units · {occupiedCount} occupied ({((occupiedCount / tenants.length) * 100).toFixed(1)}%)
              </span>
            ) : null}
          </div>
          {tenants.length === 0 ? (
            <EmptyLine>No rent roll on this deal — no unit or tenant schedule was read from the package or a sponsor workbook.</EmptyLine>
          ) : (
            <div className={`min-h-0 flex-1 ${expanded ? 'overflow-auto' : 'overflow-hidden'}`}>
              <table className="w-full border-collapse text-[9.5px]" data-panel-table>
                <thead className="sticky top-0 bg-[#0b0b0b]">
                  <tr className="text-left font-mono text-[8px] uppercase tracking-wider text-stone-600">
                    <th className="py-0.5 pr-2 font-normal">Unit</th>
                    <th className="py-0.5 pr-2 font-normal">Tenant</th>
                    <th className="py-0.5 pr-2 font-normal">Plan</th>
                    <th className="py-0.5 pr-2 text-right font-normal">SF</th>
                    <th className="py-0.5 pr-2 text-right font-normal">Rent / mo</th>
                    {showPsf ? <th className="py-0.5 pr-2 text-right font-normal">PSF</th> : null}
                    {showLeaseStart ? <th className="py-0.5 pr-2 font-normal">Start</th> : null}
                    <th className="py-0.5 pr-2 font-normal">Lease end</th>
                    {showTerm ? <th className="py-0.5 pr-2 text-right font-normal">Term</th> : null}
                    {showRehab ? <th className="py-0.5 pr-2 font-normal">Rehab</th> : null}
                    <th className="py-0.5 font-normal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(expanded ? tenants : tenants.slice(0, dashboardRows)).map((t, i) => (
                    <tr key={`${t.suite}-${t.tenant}-${i}`} className="border-t border-white/[0.03] text-stone-300">
                      <td className="py-0.5 pr-2 font-mono text-stone-400">{t.suite}</td>
                      <td className="max-w-[160px] truncate py-0.5 pr-2">{t.tenant}</td>
                      <td className="py-0.5 pr-2 text-stone-500">{t.unitType ?? '—'}</td>
                      <td className="py-0.5 pr-2 text-right font-mono">{t.sf ? t.sf.toLocaleString() : '—'}</td>
                      <td className="py-0.5 pr-2 text-right font-mono">{t.rentMo ? formatMoney(t.rentMo) : '—'}</td>
                      {showPsf ? <td className="py-0.5 pr-2 text-right font-mono">{t.rentPsf != null ? `$${t.rentPsf.toFixed(2)}` : '—'}</td> : null}
                      {showLeaseStart ? <td className="py-0.5 pr-2 font-mono text-stone-400">{t.leaseStart}</td> : null}
                      <td className="py-0.5 pr-2 font-mono text-stone-400">{t.leaseEnd}</td>
                      {showTerm ? <td className="py-0.5 pr-2 text-right font-mono">{t.termMonths != null ? `${t.termMonths} mo` : '—'}</td> : null}
                      {showRehab ? <td className="py-0.5 pr-2 text-stone-400">{t.rehab ?? '—'}</td> : null}
                      <td className={`py-0.5 ${/vacant/i.test(t.status) ? 'text-red-300/80' : 'text-stone-400'}`}>{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!expanded && tenants.length > dashboardRows ? (
                <div className="pt-0.5 font-mono text-[8px] text-stone-600">
                  +{tenants.length - dashboardRows} more units — open the panel for the full roll
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      )}
    </DossierPanel>
  )
}

function LeaseExpirationPanel({ profile }: { profile: PropertyProfile }) {
  const rows = useMemo(() => buildLeaseExpirationRows(profile), [profile])
  return (
    <DossierPanel title="Lease expiration schedule" scroll={false} dense className="h-full min-h-[220px] xl:min-h-0">
      {rows.length === 0 && isLodging(profile.propertyType) ? (
        <EmptyLine>Not applicable — hotel rooms are sold by the night, not leased.</EmptyLine>
      ) : rows.length === 0 ? (
        <EmptyLine>No lease expiration schedule is recorded.</EmptyLine>
      ) : (
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 34, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis type="category" dataKey="year" width={42} tick={{ fill: STONE, fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.025)' }}
                  contentStyle={{ background: '#0a0a0a', border: '1px solid rgba(217,183,104,0.2)', fontSize: 10 }}
                  formatter={(value) => [`${Number(value).toFixed(1)}%`, `Share of ${rows[0].basis}`]}
                />
                <Bar dataKey="pct" fill={GOLD} radius={[0, 3, 3, 0]} isAnimationActive={false}>
                  <LabelList dataKey="pct" position="right" fill="#a8a29e" fontSize={8} formatter={(value: unknown) => `${Number(value).toFixed(1)}%`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="shrink-0 pt-1 text-[9px] text-stone-600">Share of recorded {rows[0].basis} by lease-end year.</p>
        </div>
      )}
    </DossierPanel>
  )
}

/** A third-party report on file for this deal, newest first. */
type ReportOnFile = { documentType: string; party: string | null; effectiveDate: string | null; filename: string }

// Word edges written out rather than \b: desk file names join words with "_",
// which \b treats as part of the word, so "Calloway_PCA_2026.pdf" never matched.
const PCA_RE = /property[\s_-]*condition|(?:^|[^a-z0-9])pca(?:[^a-z0-9]|$)/i
const PHASE_I_RE = /phase[\s_-]*(?:i|1)(?![a-z0-9])|environmental[\s_-]*site[\s_-]*assessment|(?:^|[^a-z0-9])esa(?:[^a-z0-9]|$)/i

function reportOnFile(documents: DealDocument[], pattern: RegExp, fallbackType: string): ReportOnFile | null {
  const matches = documents
    .map((document) => {
      const fields = (document.extractedFields ?? {}) as Record<string, unknown>
      const declared = recordedText(fields.documentType)
      const hit = (declared && pattern.test(declared)) || pattern.test(document.filename)
      return hit
        ? {
            documentType: declared && pattern.test(declared) ? declared : fallbackType,
            party: recordedText(fields.party),
            effectiveDate: recordedText(fields.effectiveDate),
            filename: document.filename,
            at: Date.parse(document.createdAt) || 0,
          }
        : null
    })
    .filter((row): row is ReportOnFile & { at: number } => row != null)
    .sort((a, b) => b.at - a.at)
  if (matches.length === 0) return null
  const { documentType, party, effectiveDate, filename } = matches[0]
  return { documentType, party, effectiveDate, filename }
}

/**
 * Risk & quality, from sources only.
 *
 * The panel used to read a risk score, level, quality grade and confidence
 * from custom fields nothing on the platform computes — so every real deal
 * showed "n/a" and dashes, and the control deal a seeded "78% / A-" with no
 * method behind it. It also fixed Condition and Environmental at "Not on
 * record" even with the PCA and Phase I uploaded (stress test PROP-11).
 *
 * It now says there is no scoring method, and reports the condition and
 * environmental reports that ARE on file. Their findings are not read into
 * the record, and the panel says that rather than implying a clean report.
 */
function RiskQualityPanel({
  profile,
  documents,
  onGoto,
}: {
  profile: PropertyProfile
  documents: DealDocument[]
  onGoto: () => void
}) {
  const pca = reportOnFile(documents, PCA_RE, 'Property condition report')
  // Found by its name alone, an ESA is not assumed to be a Phase I: a Phase II
  // file matches on "ESA" and must not read as the Phase I.
  const phaseI = reportOnFile(documents, PHASE_I_RE, 'Environmental site assessment')
  const tileOf = (report: ReportOnFile | null) =>
    report
      ? {
          documentType: report.documentType,
          note: [report.party, report.effectiveDate ? formatDate(report.effectiveDate) : null].filter(Boolean).join(' · ') || report.filename,
        }
      : null
  // The recorded zone, or the reason there is none (a lookup switched off, no
  // coordinate): the reason is a hover, not a paragraph in the slot.
  const flood = recordedText(profile.floodZone)
  return (
    <DossierPanel title="Risk & quality" scroll={false} action="Documents" onAction={onGoto} dense className="h-full min-h-0">
      <div className="space-y-1.5">
        <div className="grid grid-cols-3 gap-1.5">
          <ReportStatusTile
            label="Condition"
            icon={CONDITION_ICON}
            report={tileOf(pca)}
            missingText="No property condition report uploaded"
            shortName="PCA"
          />
          <ReportStatusTile
            label="Environmental"
            icon={ENVIRONMENTAL_ICON}
            report={tileOf(phaseI)}
            missingText="No Phase I uploaded"
            shortName="Phase I ESA"
          />
          <FloodZoneTile text={flood} reason={flood ? plainRefusal(flood) : null} />
        </div>
        <p className="text-[8px] leading-snug text-stone-600" data-testid="risk-no-score">
          No risk score: no scoring method is defined for properties.
          {pca || phaseI ? ' Report findings are not read into the record — open them on Documents.' : ''}
        </p>
      </div>
    </DossierPanel>
  )
}

/** Location facts from public sources, each one carrying its source.
 *
 *  This panel exists because the fields it shows — county, FEMA flood zone,
 *  submarket demographics, transit and schools — read "Not on record" on every
 *  deal before there was anything to read them from. A source that could not
 *  answer is shown as unanswered with the reason, never as a blank that a
 *  reader would take for "none". */
/** One icon per nearby category, in the order the tiles read. */
const NEARBY_ICONS: Record<string, LucideIcon> = {
  grocery: ShoppingCart,
  dining: Utensils,
  retail: ShoppingBag,
  pharmacy: Pill,
  transit: TrainFront,
  school: School,
  university: GraduationCap,
  healthcare: HeartPulse,
  park: Trees,
}

const METERS_PER_MILE = 1609.344

/** Each distinct thing the sources said, with the sources that said it — a
 *  failure's reason or a success's caveat alike. */
function sourceNotes(sources: IntelSource[]): { detail: string; sources: string[] }[] {
  const byDetail = new Map<string, string[]>()
  for (const source of sources) {
    const detail = plainRefusal(source.detail ?? (source.ok ? '' : 'no answer')).trim()
    if (!detail) continue
    byDetail.set(detail, [...(byDetail.get(detail) ?? []), source.source])
  }
  return [...byDetail.entries()].map(([detail, names]) => ({ detail, sources: names }))
}

function radiusLabel(meters: number | null | undefined): string {
  if (!meters) return ''
  const miles = meters / METERS_PER_MILE
  return miles >= 1 ? `${miles.toFixed(miles % 1 ? 1 : 0)} mi` : `${Math.round(meters)} m`
}

function SiteIntelligencePanel({
  intel,
  loading,
  master,
  msa,
  narrative,
  matchedBy,
}: {
  intel: PropertySiteIntel | null | undefined
  loading?: boolean
  master?: DossierPropertyMaster | null
  /** The deal's own MSA — the master's metro is null on most deals. */
  msa?: string | null
  /** The market prose the package stated — the deal's field, else the intake record's (UI-01). */
  narrative?: string | null
  /** How the property master was linked to this deal (UI-09). */
  matchedBy?: DossierLinkBasis
}) {
  // Everything the sources said, grouped by what they said. Only failures
  // were shown, and only the first in the empty state: a Census "No US address
  // matched" is recorded as an answer, so the real reason every other source
  // was blank never reached the screen, and a successful source's caveat
  // ("Illustrative demo data") was dropped from its figures (PROP-20).
  const notes = sourceNotes(intel?.sources ?? [])
  // The metro falls back to the deal's own MSA, which is often the geocoder's
  // suggestion: it carries that field's state, never reads as a public record
  // fact (C05).
  const { valueSource } = useDossierEdit()
  const metroSource = master?.metro ? null : valueSource?.('msa') ?? null

  const facts: {
    label: string
    value: string | null
    note?: string | null
    icon: LucideIcon
    source?: FieldSourceRef | null
    /** The deal field the value IS, when it is the deal's own statement
     *  rather than a public source's answer — editable in place. */
    field?: string
  }[] = [
    { label: 'County', value: intel?.county ?? null, icon: LandPlot },
    // The master this deal was linked to, and how: a link by name and market
    // is the platform's match, not the deal's statement (UI-09).
    {
      label: 'Property master',
      value: master ? master.name : null,
      note: master
        ? matchedBy === 'name'
          ? 'linked by name and market — confirm the link'
          : matchedBy === 'id'
            ? 'linked by the deal’s property id'
            : null
        : null,
      icon: Building2,
    },
    {
      label: 'Flood zone',
      value: intel?.floodZone ?? null,
      note: plainRefusal(intel?.floodZoneDetail),
      icon: Waves,
    },
    {
      label: 'Population',
      value: intel?.population != null ? intel.population.toLocaleString() : null,
      note: intel?.populationGeography ?? null,
      icon: Users,
    },
    {
      label: 'Population growth',
      value:
        intel?.populationGrowthPct != null
          ? `${intel.populationGrowthPct > 0 ? '+' : ''}${intel.populationGrowthPct.toFixed(1)}%`
          : null,
      note: intel?.populationGrowthPct != null ? 'ACS 5-year, 5-year change' : null,
      icon: TrendingUp,
    },
    {
      label: 'Households',
      value: intel?.households != null ? intel.households.toLocaleString() : null,
      note: intel?.populationGeography ?? null,
      icon: Home,
    },
    {
      label: 'Median age',
      value: intel?.medianAge != null ? intel.medianAge.toFixed(1) : null,
      note: intel?.populationGeography ?? null,
      icon: Users,
    },
    {
      label: 'Median household income',
      value:
        intel?.medianHouseholdIncome != null
          ? `$${intel.medianHouseholdIncome.toLocaleString()}`
          : null,
      icon: DollarSign,
    },
    {
      label: 'Household growth',
      value:
        intel?.householdGrowthPct != null
          ? `${intel.householdGrowthPct > 0 ? '+' : ''}${intel.householdGrowthPct.toFixed(1)}%`
          : null,
      note: intel?.householdGrowthPct != null ? 'ACS 5-year, 5-year change' : null,
      icon: TrendingUp,
    },
    { label: 'Submarket', value: master?.submarket ?? intel?.neighborhood ?? null, icon: Home },
    { label: 'Metro', value: master?.metro ?? msa ?? null, icon: Building2, source: metroSource, field: master?.metro ? undefined : 'msa' },
    {
      label: 'Parcel',
      value: intel?.parcelNumber ?? null,
      note: [
        intel?.parcelAcres != null ? `${intel.parcelAcres} ac` : null,
        intel?.parcelZoning ?? null,
        intel?.parcelLandUse ?? null,
      ].filter(Boolean).join(' · ') || null,
      icon: LandPlot,
    },
  ].filter((row) => row.value != null)

  // Every category the lookup resolved, not just transit and schools. A
  // count per category is the figure a reader sizes a submarket with; the
  // names are the detail beneath it. Falls back to the two named fields for
  // a payload cached before `nearby` existed.
  const radiusM = intel?.nearbyRadiusM ?? null
  const nearby = NEARBY_CATEGORY_LABELS.map(({ kind, label }) => {
    const places =
      intel?.nearby?.[kind] ??
      (kind === 'transit' ? intel?.transit : kind === 'school' ? intel?.schools : undefined) ??
      []
    const count = intel?.nearbyCounts?.[kind] ?? places.length
    const capped = intel?.nearbyCapped?.[kind] ?? false
    return { kind, label, places, count, capped, icon: NEARBY_ICONS[kind] ?? Building2 }
  })
    .filter((group) => group.count > 0)
    // Four to a row with Schools closing the grid, so the eight everyday
    // categories stand 4 × 2 (Schools under Pharmacy) and the column they
    // gave up goes to the public record beside them.
    .sort((a, b) => Number(a.kind === 'school') - Number(b.kind === 'school'))

  const ring = radiusLabel(radiusM)
  const density = intel?.nearbyDensity ?? null
  const prose = narrative?.trim() || null
  // The package's market prose, under the public record it describes. It was
  // extracted on every OM and printed nowhere: its one render block was gated
  // on a catalogue group the Audit tab never selected (UI-01).
  const narrativeBlock = prose ? (
    <div className="mt-2 border-t border-white/[0.04] pt-1.5" data-testid="market-narrative">
      <div className="mb-0.5 font-mono text-[8px] uppercase tracking-widest text-stone-600">
        Market narrative · stated in the package
      </div>
      <p className="whitespace-pre-wrap text-[10px] leading-snug text-stone-300">{prose}</p>
    </div>
  ) : null

  return (
    <DossierPanel title="Location intelligence" landscape scroll={false} dense className="h-full min-h-[180px] xl:min-h-0">
      {({ expanded }) => expanded ? (
        <>
          <LocationIntelligenceDetail intel={intel} />
          {narrativeBlock}
        </>
      ) : loading ? (
        <div className="flex items-center gap-1.5 py-6 text-[11px] text-stone-500">
          <Loader2 size={12} className="animate-spin" /> Resolving public records for this address…
        </div>
      ) : facts.length === 0 && nearby.length === 0 && !prose ? (
        notes.length > 0 ? (
          <ul className="space-y-1 py-4 text-center text-[11px] text-stone-500" data-testid="site-intel-reasons">
            {notes.map((note) => (
              <li key={note.detail}>
                <span className="text-stone-600">{note.sources.join(', ')}:</span> {note.detail}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyLine>
            {intel ? 'No public source answered for this address.' : 'No address on this deal — nothing to look up.'}
          </EmptyLine>
        )
      ) : (
        /* Two columns: the amenity counts fill the left, the public-record
           facts stand in a column on the right. Stacked, the tiles took one
           row and the facts were squeezed into what was left beneath them. */
        <div className="grid h-full min-h-0 gap-3 xl:grid-cols-[minmax(0,1.05fr)_minmax(220px,1fr)]">
          <div className="flex min-h-0 min-w-0 flex-col">
            {nearby.length > 0 ? (
              <>
                <div className="mb-1.5 flex shrink-0 items-baseline justify-between gap-2">
                  <span className="font-mono text-[8px] uppercase tracking-widest text-stone-600">
                    Within {ring || 'reach'}
                  </span>
                  {density ? (
                    <span className="font-mono text-[8px] uppercase tracking-widest text-stone-600">
                      {density === 'urban' ? 'Urban ring · 1.5 mi' : 'Suburban ring · 5 mi'}
                    </span>
                  ) : null}
                </div>
                <div className="grid min-h-0 flex-1 grid-cols-3 auto-rows-[minmax(64px,1fr)] gap-1.5 overflow-auto sm:grid-cols-4 xl:grid-cols-5" data-testid="nearby-tiles">
                  {nearby.map((group) => {
                    const Icon = group.icon
                    const names = group.places.slice(0, expanded ? 20 : 6).map((p) => p.name).join('\n')
                    return (
                      <div
                        key={group.kind}
                        title={names ? `${group.label} within ${ring}:\n${names}` : undefined}
                        className="flex min-h-0 min-w-0 flex-col items-center justify-center rounded-md border border-amber-400/10 bg-black/40 px-1.5 py-1.5 text-center"
                      >
                        <Icon size={24} className="shrink-0 text-amber-300/90" />
                        <span className="mt-0.5 font-mono text-[15px] font-semibold leading-none text-stone-100">
                          {group.count}
                          {group.capped ? <span className="text-[10px] text-amber-300/80">+</span> : null}
                        </span>
                        <span className="mt-0.5 w-full truncate text-[8px] uppercase tracking-wider text-stone-500">
                          {group.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : intel?.geocodePrecision === 'locality' ? (
              <p className="text-[10px] leading-snug text-stone-500" data-testid="site-intel-city-level">
                City-level address: nearby places are counted around a street address, so none are
                counted here. The public record beside this describes the city.
              </p>
            ) : (
              <EmptyLine>No nearby places on record.</EmptyLine>
            )}
            {expanded && nearby.length > 0 ? (
              /* At full size, the names behind each count. */
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                {nearby.map((group) => (
                  <div key={`${group.kind}-names`} className="min-w-0">
                    <div className="font-mono text-[8px] uppercase tracking-widest text-stone-600">{group.label}</div>
                    <ul className="mt-0.5 space-y-0.5">
                      {group.places.slice(0, 8).map((place) => (
                        <li key={`${group.kind}-${place.name}`} className="truncate text-[10px] text-stone-400" title={place.vicinity ?? undefined}>
                          {place.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex min-h-0 min-w-0 flex-col overflow-auto border-t border-white/[0.04] pt-2 xl:border-l xl:border-t-0 xl:pl-3 xl:pt-0">
            <div className="mb-1 font-mono text-[8px] uppercase tracking-widest text-stone-600">Public record</div>
            {facts.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {facts.map((row) => {
                  const Icon = row.icon
                  return (
                    <div key={row.label} className="flex min-w-0 items-start gap-1.5">
                      <Icon size={18} className="mt-0.5 shrink-0 text-amber-300/80" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-stone-600">
                          {row.label}
                          <ValueStateChip source={row.source} />
                        </div>
                        <div className="break-words font-mono text-[11px] text-stone-200">
                          {row.field ? <EditableValue name={row.field} label={row.label} display={row.value ?? '—'} /> : row.value}
                        </div>
                        {row.note ? <div className="text-[8px] text-stone-600">{row.note}</div> : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-[10px] text-stone-600">No public-record fact answered for this address.</p>
            )}
            {intel?.website ? (
              <a
                href={intel.website}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-flex max-w-full items-center gap-1 truncate text-[10px] text-amber-300/90 hover:text-amber-200"
              >
                <span className="truncate">{intel.website}</span> <ArrowRight size={10} className="shrink-0" />
              </a>
            ) : null}
            {notes.length > 0 ? (
              <ul className="mt-auto space-y-0.5 pt-2" data-testid="site-intel-notes">
                {(expanded ? notes : notes.slice(0, 2)).map((note) => (
                  <li key={note.detail} className="truncate text-[8px] text-stone-600" title={`${note.sources.join(', ')}: ${note.detail}`}>
                    {note.sources.join(', ')}: {note.detail}
                  </li>
                ))}
                {!expanded && notes.length > 2 ? (
                  <li className="text-[8px] text-stone-700">+{notes.length - 2} more — open the panel</li>
                ) : null}
              </ul>
            ) : null}
            {expanded && intel ? (
              <p className="mt-2 text-[8px] text-stone-700">
                Sources: {(intel.sources ?? []).filter((s) => s.ok).map((s) => s.source).join(', ') || 'none'}
              </p>
            ) : null}
            {narrativeBlock}
          </div>
          {/* The 1/2/3-mile ring table used to sit here and took the height
              the amenity tiles need, so the dashboard slot showed a heading
              with no tiles under it. The rings are a full-size read: they
              live in the expanded panel, beside the per-metric charts.
              `LocationIntelligenceDetail` carries them. */}
        </div>
      )}
    </DossierPanel>
  )
}

/** A comp row, from whichever supply answered — both shapes render the same.
 *  Provenance is optional because the market-search supply does not carry it:
 *  a comp from the comp database is a market observation and states its feed,
 *  while one from the deal's package states the attachment it was read from. */
type CompRow = Pick<
  CompRecord,
  | 'id'
  | 'propertyName'
  | 'address'
  | 'city'
  | 'numberOfUnits'
  | 'yearBuilt'
  | 'occupancyAtSale'
  | 'pricePerSf'
  | 'salePrice'
  | 'capRate'
> & {
  lat?: number | null
  lng?: number | null
  dataSource?: string | null
  sourceDocument?: string | null
  suppliedWithDeal?: boolean
  transactionType?: string | null
  sourceExtras?: DossierComparable['sourceExtras']
}

function ComparablesSummaryPanel({
  raw,
  profile,
  comparables,
  packageCompTablesUnread,
  addressParts,
  mapLat,
  mapLng,
  area,
}: {
  raw: RawDealFile
  profile: PropertyProfile
  comparables?: DossierComparable[] | null
  packageCompTablesUnread?: { document: string; heading: string }[]
  addressParts?: { city: string | null; state: string | null } | null
  mapLat?: number | null
  mapLng?: number | null
  /** The deal's stated city, for a deal with no street: the map opens there. */
  area?: { lat: number; lng: number; label: string } | null
}) {
  const { effectiveValue, valueSource } = useDossierEdit()
  // The subject's cap rate is the deal's cap_rate field: it carries that
  // field's value state here as it does on the tile (C05), so a derived
  // NOI / value never reads as a stated figure beside the comps.
  const subjectCapChip = recordedText(profile.capRate)
    ? <ValueStateChip source={valueSource?.('cap_rate') ?? null} />
    : null
  const assetType = recordedText(profile.propertyType) ?? undefined
  // The market this deal is in. A comp in another state is not a comparable,
  // and a national list of one asset type presented under this heading reads
  // as though the market had been analysed. The server-resolved set is already
  // scoped; the fallback search is scoped here so it cannot be broader. The
  // deal's own city and state first — the location string is a street address
  // on most intake deals. Both come from the settled address (name-lookup
  // values withheld), never the deal file's raw city, state or location: on a
  // legacy deal those name a town Google Places invented.
  const [locationCity, locationState] = splitLocation(recordedText(profile.location))
  const settledState = recordedText(addressParts?.state)?.toUpperCase() ?? null
  // A deal that states only its state has "GA" for a location, which reads as a
  // city to a parser that cannot tell the two apart.
  const city =
    recordedText(addressParts?.city)
    ?? (locationCity && locationCity.toUpperCase() !== settledState ? locationCity : null)
  const state = settledState ?? locationState
  const serverScoped = Array.isArray(comparables)
  const comps = useQuery({
    queryKey: ['comps-search', assetType, city, state],
    queryFn: () => compsService.search({ assetType, city: city ?? undefined, state: state ?? undefined }),
    enabled: !serverScoped && Boolean(assetType) && Boolean(city || state),
    staleTime: 5 * 60_000,
  })
  // Every comparable, not the first six. The panel scrolls; a silent truncation
  // under a heading that says "Comparables summary" reads as the whole set.
  const rows: CompRow[] = serverScoped ? (comparables ?? []) : (comps.data ?? [])
  const subjectValue = numeric(effectiveValue('property_value') ?? rawValue(raw, 'property_value'))
  const subjectSf = numeric(effectiveValue('sf') ?? rawValue(raw, 'sf'))
  const subjectPricePerSf =
    subjectValue && subjectSf && subjectSf > 0
      ? `$${(subjectValue / subjectSf).toFixed(2)}`
      : null
  const loading = !serverScoped && comps.isLoading
  const failed = !serverScoped && comps.isError
  const noMarket = !serverScoped && !city && !state
  const saleRows = rows.filter((comp) => (comp.transactionType ?? 'sale') !== 'rent')
  const rentRows = rows.filter((comp) => comp.transactionType === 'rent')
  const [compKind, setCompKind] = useState<'sale' | 'rent'>('sale')
  const shownRows = compKind === 'rent' && rentRows.length > 0 ? rentRows : saleRows.length > 0 ? saleRows : rows
  // A set with no sale in it is a rent set, whichever toggle is selected.
  const showingRent = compKind === 'rent' || (saleRows.length === 0 && rentRows.length > 0)
  const showParties = shownRows.some((comp) => extraText(comp, 'buyer', 'purchaser') || extraText(comp, 'seller', 'grantor'))
  // A comparable with a coordinate is a marker; selecting it (a row, or the
  // marker) flies the map there and sets it beside the subject.
  const [selectedCompId, setSelectedCompId] = useState<string | null>(null)
  // The dashboard slot opens on the list beside the map (Edward, 30 Oct 2026:
  // the comparables listed on the left, plotted on a map on the right around
  // the subject in red); the full table is one click away. The full-size view
  // shows the map above the table.
  const [view, setView] = useState<'split' | 'table'>('split')
  const viewToggle = (
    <span className="flex items-center gap-0.5" role="group" aria-label="Comparables view">
      {([
        ['split', 'Map and list view', MapIcon],
        ['table', 'Table view', Table2],
      ] as const).map(([id, label, Icon]) => (
        <button
          key={id}
          type="button"
          aria-label={label}
          title={label}
          aria-pressed={view === id}
          onClick={() => setView(id)}
          className={`rounded p-1 transition-colors ${
            view === id ? 'bg-amber-300/15 text-amber-200' : 'text-stone-500 hover:text-amber-200'
          }`}
        >
          <Icon size={12} aria-hidden="true" />
        </button>
      ))}
    </span>
  )
  const subjectPoint: MapPoint | null =
    mapLat != null && mapLng != null
      ? { id: '__subject', name: profile.propertyName, lat: mapLat, lng: mapLng, detail: recordedText(profile.fullAddress) }
      : null
  const compPoints: MapPoint[] = rows.flatMap((comp) =>
    typeof comp.lat === 'number' && typeof comp.lng === 'number'
      ? [{ id: comp.id, name: comp.propertyName, lat: comp.lat, lng: comp.lng, detail: comp.address ?? comp.city ?? null }]
      : [],
  )
  const compsMap = (className: string) => (
    <CompsMap subject={subjectPoint} area={area} comps={compPoints} selectedId={selectedCompId} onSelect={setSelectedCompId} className={className} />
  )
  const emptyNote = (
    <>
      <p className="py-1 text-[10px] text-stone-400" data-testid="comps-empty">
        No comparables on file for {assetType ?? 'this asset type'} in
        {' '}{[city, state].filter(Boolean).join(', ') || 'this market'}. The comp database holds
        the deal's own package comps and entered comps; no market data feed is connected.
      </p>
      {(packageCompTablesUnread ?? []).map((table) => (
        <p key={`${table.document}-${table.heading}`} className="text-[10px] text-amber-300/80" data-testid="comps-unread">
          {table.document} has a comp table (“{table.heading}”) the platform could not read — open it on Documents.
        </p>
      ))}
    </>
  )
  const comparison = selectedCompId !== null ? (() => {
    const comp = rows.find(row => row.id === selectedCompId)
    return comp ? <div className="mb-2 shrink-0 rounded border border-amber-300/20 p-2 text-[11px]" data-testid="comparable-comparison">
      <div className="mb-1 flex justify-between"><strong>{comp.propertyName} vs. {profile.propertyName}</strong><button type="button" onClick={() => setSelectedCompId(null)} aria-label="Close comparison">×</button></div>
      <table className="w-full text-left"><thead><tr><th>Metric</th><th>Comparable</th><th>Subject</th></tr></thead><tbody>
        <tr><td>Address</td><td>{comp.address ?? comp.city ?? '—'}</td><td>{profile.fullAddress ?? profile.location}</td></tr>
        <tr><td>Units</td><td>{comp.numberOfUnits ?? '—'}</td><td>{profile.totalUnits ?? '—'}</td></tr>
        <tr><td>Year built</td><td>{comp.yearBuilt ?? '—'}</td><td>{profile.yearBuilt || '—'}</td></tr>
        <tr><td>Occupancy</td><td>{comp.occupancyAtSale != null ? formatPct(comp.occupancyAtSale) : '—'}</td><td>{profile.occupancy}</td></tr>
        {comp.transactionType === 'rent' ? <tr><td>Recorded asking rent</td><td>{extraText(comp, 'asking rent', 'avg rent', 'avg rent/unit', 'rent/unit') || '—'}</td><td>Compare source rent units and period</td></tr> : <>
          <tr><td>Price / SF</td><td>{comp.pricePerSf != null ? formatMoney(comp.pricePerSf) : '—'}</td><td>{subjectPricePerSf ?? '—'}</td></tr>
          <tr><td>Sale price / subject value</td><td>{formatAmount(comp.salePrice)}</td><td>{formatAmount(subjectValue)}</td></tr>
        </>}
        <tr><td>Cap rate</td><td>{comp.capRate != null ? formatPct(comp.capRate) : '—'}</td><td><span className="inline-flex items-center gap-1">{subjectCapChip}{profile.capRate}</span></td></tr>
      </tbody></table><p className="mt-1 text-stone-400">Source: {comp.sourceDocument ?? comp.dataSource ?? 'Not recorded'}. Comparable observations may have a different valuation date.</p>
    </div> : null
  })() : null
  // Where the subject is, at the precision the deal states it: the building's
  // address when a street is on record, the city when only that is.
  const subjectWhere = subjectPoint
    ? recordedText(profile.fullAddress) ?? 'Pinned on the map'
    : area
      ? `${area.label} · city, not the site`
      : 'No location on record'
  // The list beside the map: each comparable's name and address, and the one
  // figure it is compared on. A row with a coordinate selects its marker.
  const compList = (
    <div className="flex min-h-0 min-w-0 flex-col" data-testid="comps-list">
      <div className="mb-1 flex shrink-0 items-start gap-1.5 rounded border border-red-500/25 bg-red-500/[0.06] px-1.5 py-1" data-testid="comps-list-subject">
        <span
          aria-hidden="true"
          className={`mt-[3px] inline-block h-2 w-2 shrink-0 rounded-full ${subjectPoint ? 'bg-red-600' : 'border-2 border-red-600'}`}
        />
        <span className="min-w-0">
          <span className="block truncate text-[11px] font-semibold text-stone-100">Subject: {profile.propertyName}</span>
          <span className="block truncate text-[10px] text-stone-400">{subjectWhere}</span>
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {comparison}
        {rows.length === 0 ? emptyNote : (
          <ul className="divide-y divide-white/[0.04]">
            {rows.map((comp) => {
              const mapped = typeof comp.lat === 'number' && typeof comp.lng === 'number'
              const active = comp.id === selectedCompId
              const figure = comp.transactionType === 'rent'
                ? extraMoney(comp, 'asking rent', 'avg rent', 'avg rent/unit', 'rent/unit') ?? 'Rent comp'
                : comp.salePrice != null ? formatMoney(comp.salePrice) : null
              return (
                <li key={comp.id}>
                  <button
                    type="button"
                    disabled={!mapped}
                    aria-pressed={mapped ? active : undefined}
                    onClick={() => setSelectedCompId(active ? null : comp.id)}
                    title={mapped ? 'Show on the map' : 'No coordinate for this comparable yet'}
                    className={`flex w-full min-w-0 items-start gap-1.5 px-1 py-1 text-left ${
                      mapped ? 'hover:bg-white/[0.03]' : 'cursor-default'
                    } ${active ? 'bg-amber-300/10' : ''}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-[4px] inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                        mapped ? (active ? 'bg-amber-300' : 'bg-stone-400') : 'ring-1 ring-stone-600'
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11px] text-stone-200">{comp.propertyName}</span>
                      <span className="block truncate text-[10px] text-stone-500">{comp.address ?? textOrDash(comp.city)}</span>
                    </span>
                    {figure ? <span className="shrink-0 pt-[1px] font-mono text-[10px] text-stone-300">{figure}</span> : null}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
  // Rows select their marker only where the map is on screen with them (full size).
  const table = (expanded: boolean) => (
    <>
        {saleRows.length > 0 && rentRows.length > 0 ? (
          <div className="mb-1.5 flex shrink-0 gap-1">
            {(['sale', 'rent'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                aria-pressed={compKind === kind}
                onClick={() => setCompKind(kind)}
                className={`rounded border px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider ${
                  compKind === kind ? 'border-amber-300/30 bg-amber-300/[0.06] text-amber-200' : 'border-stone-800 text-stone-500'
                }`}
              >
                {kind === 'sale' ? `Sale (${saleRows.length})` : `Rent (${rentRows.length})`}
              </button>
            ))}
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[720px] text-left text-[10px]">
            <thead className="font-mono text-[8px] uppercase tracking-widest text-stone-600">
              <tr>
                <th className="pb-1.5 font-normal">Property</th>
                <th className="pb-1.5 font-normal">Address</th>
                <th className="pb-1.5 text-right font-normal">Units</th>
                <th className="pb-1.5 text-right font-normal">Built</th>
                <th className="pb-1.5 text-right font-normal">Occ.</th>
                <th className="pb-1.5 text-right font-normal">$/SF</th>
                <th className="pb-1.5 text-right font-normal">{showingRent ? 'Asking rent' : 'Sale price'}</th>
                <th className="pb-1.5 text-right font-normal">Cap rate</th>
                {showParties ? <th className="pb-1.5 font-normal">Buyer</th> : null}
                {showParties ? <th className="pb-1.5 font-normal">Seller</th> : null}
                <th className="pb-1.5 font-normal">Source</th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((comp) => {
                const mapped = typeof comp.lat === 'number' && typeof comp.lng === 'number'
                const active = expanded && comp.id === selectedCompId
                return (
                <tr
                  key={comp.id}
                  onClick={expanded && mapped ? () => setSelectedCompId(comp.id === selectedCompId ? null : comp.id) : undefined}
                  className={`border-t border-white/[0.04] text-stone-300 ${
                    expanded && mapped ? 'cursor-pointer hover:bg-white/[0.03]' : ''
                  } ${active ? 'bg-amber-300/10' : ''}`}
                  aria-selected={active || undefined}
                  title={expanded ? (mapped ? 'Show on the map' : 'No coordinate for this comparable yet') : undefined}
                >
                  <td className="py-1.5">
                    {expanded ? (
                      <span
                        className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${
                          mapped ? (active ? 'bg-amber-200' : 'bg-stone-400') : 'bg-transparent ring-1 ring-stone-700'
                        }`}
                      />
                    ) : null}
                    {comp.propertyName}
                  </td>
                  {/* A comparable's address opens the map on that building —
                      the fastest way to sanity-check whether it is comparable. */}
                  <td className="max-w-[220px] truncate text-stone-500">
                    <AddressLink address={comp.address} fallback={textOrDash(comp.city)} />
                  </td>
                  <td className="text-right font-mono">{comp.numberOfUnits ?? '—'}</td>
                  <td className="text-right font-mono">{comp.yearBuilt ?? '—'}</td>
                  <td className="text-right font-mono">{comp.occupancyAtSale != null ? formatPct(comp.occupancyAtSale) : '—'}</td>
                  <td className="text-right font-mono">{comp.pricePerSf != null ? `$${comp.pricePerSf.toFixed(2)}` : extraText(comp, 'price/sf', '$/sf') || '—'}</td>
                  <td className="text-right font-mono">
                    {showingRent
                      ? extraMoney(comp, 'asking rent', 'avg rent', 'avg rent/unit', 'rent/unit') || (comp.salePrice != null ? formatMoney(comp.salePrice) : '—')
                      : (comp.salePrice != null ? formatMoney(comp.salePrice) : '—')}
                  </td>
                  <td className="text-right font-mono">{comp.capRate != null ? formatPct(comp.capRate) : '—'}</td>
                  {showParties ? <td className="max-w-[140px] truncate text-stone-400">{extraText(comp, 'buyer', 'purchaser') || '—'}</td> : null}
                  {showParties ? <td className="max-w-[140px] truncate text-stone-400">{extraText(comp, 'seller', 'grantor') || '—'}</td> : null}
                  {/* Where the figures came from. A comp the sponsor supplied
                      is a claim in their package, not a market observation,
                      and the row should say which it is. */}
                  <td className="max-w-[160px] truncate text-stone-500" title={comp.sourceDocument ?? undefined}>
                    {comp.suppliedWithDeal ? (comp.sourceDocument ?? 'Deal package') : (comp.dataSource ?? '—')}
                  </td>
                </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-amber-300/15 text-amber-100/90">
                <td className="pt-2 font-semibold">Subject: {profile.propertyName}</td>
                <td />
                <td className="pt-2 text-right font-mono">{profile.totalUnits ?? '—'}</td>
                <td className="pt-2 text-right font-mono">{profile.yearBuilt || '—'}</td>
                <td className="pt-2 text-right font-mono">{profile.occupancy}</td>
                {/* The comp rows carry SALE price per SF; the subject line has
                    to be the same quantity. It read the subject's monthly rent
                    per SF, so one column compared a rent against three prices. */}
                <td className="pt-2 text-right font-mono">{subjectPricePerSf ?? '—'}</td>
                <td className="pt-2 text-right font-mono">{formatAmount(effectiveValue('property_value') ?? profile.stabilizedValueAmount ?? rawValue(raw, 'property_value'))}</td>
                <td className="pt-2 text-right font-mono">
                  <span className="inline-flex items-center justify-end gap-1">{subjectCapChip}{profile.capRate}</span>
                </td>
                {showParties ? <td /> : null}
                {showParties ? <td /> : null}
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
    </>
  )
  return (
    <DossierPanel title="Comparables summary" landscape action="Full comps" headerExtra={viewToggle} dense scroll={false} className="h-full min-h-[300px] xl:min-h-0">
      {({ expanded }) => loading ? (
        <div className="flex items-center gap-1.5 py-6 text-[11px] text-stone-500">
          <Loader2 size={12} className="animate-spin" /> Searching the comparable database…
        </div>
      ) : failed ? (
        <EmptyLine>Comparable search is unavailable.</EmptyLine>
      ) : noMarket ? (
        <EmptyLine>No market on this deal — comparables are only shown within the deal's own market.</EmptyLine>
      ) : expanded ? (
        <div className="flex h-full min-h-0 flex-col">
          {compsMap(rows.length ? 'mb-2 h-[360px] shrink-0' : 'h-[480px] shrink-0')}
          {rows.length ? <>{comparison}{table(true)}</> : emptyNote}
        </div>
      ) : view === 'table' ? (
        <div className="flex h-full min-h-0 flex-col overflow-y-auto">
          {rows.length ? table(false) : emptyNote}
        </div>
      ) : (
        <div className="grid h-full min-h-0 grid-cols-1 gap-1.5 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]" data-testid="comps-split">
          {compList}
          {compsMap('min-h-[160px] xl:min-h-0')}
        </div>
      )}
    </DossierPanel>
  )
}

/** The deal's stated city as a place to open a map on, when that is all it states. */
function cityArea(props: Pick<Props, 'mapBasis' | 'cityLat' | 'cityLng' | 'addressParts' | 'profile'>) {
  if (props.mapBasis !== 'city' || props.cityLat == null || props.cityLng == null) return null
  const label =
    [recordedText(props.addressParts?.city), recordedText(props.addressParts?.state)].filter(Boolean).join(', ')
    || recordedText(props.profile.location)
    || 'City'
  return { lat: props.cityLat, lng: props.cityLng, label }
}

export function PropertyPage(props: Props) {
  // The metric bar above is the dossier's one header (DealDossier); this tab
  // adds no tiles to it. It published NOI in-place / stabilized, which the
  // client ruled off the header (Chris Young, 2 Nov).
  // Two halves, each its own stack, at full width. The page was three rows
  // across the whole width, so Comparables could be no taller than the row it
  // shared with Location intelligence (a quarter of the page). The right half
  // now stacks its panels at the height they need and gives Comparables the
  // rest (Edward, 30 Oct 2026).
  return (
    <div
      data-testid="property-dashboard"
      className="dossier-property-grid flex h-full min-h-0 flex-col gap-1.5 overflow-y-auto pb-1 xl:grid xl:grid-cols-2 xl:grid-rows-[minmax(0,1fr)] xl:overflow-hidden xl:pb-0"
    >
      <div className="flex min-h-0 min-w-0 flex-col gap-1.5" data-testid="property-left">
        <div className="grid gap-1.5 md:grid-cols-2 xl:min-h-0 xl:flex-[1.15]">
          <div className="min-h-0 min-w-0"><PropertyPhotosPanel {...props} /></div>
          <div className="min-h-0 min-w-0"><LocationPanel {...props} locating={Boolean(props.mediaLoading || props.siteIntelLoading)} /></div>
        </div>
        <div className="min-h-0 min-w-0 xl:flex-[1.2]">
          {isLodging(props.profile.propertyType) ? (
            <HotelOperationsPanel
              raw={props.raw}
              profile={props.profile}
              occupancyHistory={props.propertyMaster?.occupancyHistory ?? null}
            />
          ) : (
            <RentRollSummaryPanel
              raw={props.raw}
              profile={props.profile}
              occupancyHistory={props.propertyMaster?.occupancyHistory ?? null}
            />
          )}
        </div>
        {/* Half the page wide. At a third, nine amenity counts and eight public
            facts were a single column of ten-point rows nobody could read. */}
        <div className="min-h-0 min-w-0 xl:flex-[0.75]">
          <SiteIntelligencePanel
            intel={props.siteIntel}
            loading={props.siteIntelLoading}
            master={props.propertyMaster}
            msa={recordedText(rawValue(props.raw, 'msa'))}
            narrative={props.profile.marketNarrative ?? props.marketNarrative ?? null}
            matchedBy={props.propertyMatchedBy}
          />
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-col gap-1.5" data-testid="property-right">
        {/* Across the half, at the height its tiles need: eight tiles in a
            quarter-width column cut every label short. */}
        <div className="min-w-0 shrink-0"><KeyMetricsPanel profile={props.profile} onGoto={() => props.onGoto('economics')} /></div>
        {/* A short window (1440x900) gives this block a larger share, so Crime &
            safety keeps room for its tiles; Comparables still has more than the
            quarter of the page it had. */}
        <div className="grid gap-1.5 md:grid-cols-2 xl:min-h-0 xl:flex-[1.2] xl:[@media(max-height:960px)]:flex-[1.6]">
          <div className="flex min-h-0 min-w-0 flex-col gap-1.5">
            <div className="min-h-0 min-w-0 xl:flex-[1.15]"><PropertySummaryPanel profile={props.profile} addressStatus={props.mapBasis} addressUnchecked={props.addressUnchecked} addressPending={props.addressPending} addressStateOnly={props.addressStateOnly} addressEditable={props.addressEditable} onGoto={() => props.onGoto('property')} /></div>
            <div className="min-h-0 min-w-0 xl:flex-1"><LeaseExpirationPanel profile={props.profile} /></div>
          </div>
          {/* Risk & quality takes the height its tiles need; Crime & safety
              takes what is left. Recent activity was a panel in this column
              once and left the others too short to read; the full activity
              log lives on the Audit tab. */}
          <div className="flex min-h-0 min-w-0 flex-col gap-1.5">
            <div className="min-w-0 shrink-0"><RiskQualityPanel profile={props.profile} documents={props.documents} onGoto={() => props.onGoto('documents')} /></div>
            <div className="min-h-0 min-w-0 xl:flex-1">
              <CrimeSafetyPanel state={props.siteIntel?.stateCode ?? recordedText(props.addressParts?.state)?.toUpperCase() ?? splitLocation(recordedText(props.profile.location))[1]} />
            </div>
          </div>
        </div>
        <div className="min-h-0 min-w-0 xl:flex-1">
          <ComparablesSummaryPanel
            raw={props.raw}
            profile={props.profile}
            comparables={props.comparables}
            packageCompTablesUnread={props.packageCompTablesUnread}
            addressParts={props.addressParts}
            // The subject pin is the building; a city centre or an address
            // nothing supports is not drawn as one.
            mapLat={(props.mapBasis ?? 'asset') === 'asset' ? props.mapLat : null}
            mapLng={(props.mapBasis ?? 'asset') === 'asset' ? props.mapLng : null}
            area={cityArea(props)}
          />
        </div>
      </div>
    </div>
  )
}

export default PropertyPage
